// Live scheduler health card — shows on AdminOverviewPage. Confirms the
// platform's hourly auto-sync and stuck-call reaper jobs are firing, and
// gives the admin a one-click "Run sync now" lever if anything looks stale.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Activity, RefreshCw, Play, Wrench, Loader2, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatRelative = (iso) => {
  if (!iso) return 'never';
  try {
    const diffMs = new Date(iso) - new Date();
    const absMin = Math.abs(diffMs) / 60000;
    if (absMin < 1) return diffMs < 0 ? 'just now' : 'in <1m';
    if (absMin < 60) {
      const m = Math.round(absMin);
      return diffMs < 0 ? `${m}m ago` : `in ${m}m`;
    }
    const h = Math.round(absMin / 60);
    return diffMs < 0 ? `${h}h ago` : `in ${h}h`;
  } catch {
    return '—';
  }
};

const JobRow = ({ job }) => {
  const h = job.history || {};
  const hasError = !!h.last_error;
  const nextLabel = formatRelative(job.next_run);
  const lastLabel = h.last_started ? formatRelative(h.last_started) : 'not yet';
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-white/5">
      <div className={`w-2 h-2 rounded-full ${hasError ? 'bg-rose-400' : 'bg-emerald-400'}`} />
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium truncate">{job.name || job.id}</p>
        <p className="text-white/30 text-[11px] truncate">
          {h.runs || 0} run{(h.runs || 0) === 1 ? '' : 's'} · last {lastLabel}
          {hasError && <span className="text-rose-400 ml-2">⚠ {h.last_error.slice(0, 40)}</span>}
        </p>
      </div>
      <div className="text-right">
        <p className="text-white/60 text-xs">next</p>
        <p className="text-white text-xs font-medium">{nextLabel}</p>
      </div>
    </div>
  );
};

const Lever = ({ icon: Icon, label, onClick, disabled, testId }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
    data-testid={testId}
  >
    {disabled ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
    {label}
  </button>
);

export const SchedulerHealthCard = ({ token }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [firing, setFiring] = useState(null); // 'sync' | 'reap' | null

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/scheduler/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(r.data);
    } catch (e) {
      console.error('Failed to fetch scheduler status', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 30000);
    return () => clearInterval(t);
  }, [fetchStatus]);

  const fire = async (key, url, label) => {
    setFiring(key);
    try {
      await axios.post(`${API}${url}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${label} dispatched`);
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${label} failed`);
    } finally {
      setFiring(null);
    }
  };

  if (loading || !status) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-xl p-5">
        <div className="flex items-center gap-2 text-white/40 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading scheduler status…
        </div>
      </div>
    );
  }

  const ok = status.running;

  return (
    <div
      className="bg-white/5 border border-white/10 rounded-xl overflow-hidden"
      data-testid="scheduler-health-card"
    >
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className={`w-5 h-5 ${ok ? 'text-emerald-400' : 'text-rose-400'}`} />
          <div>
            <h3 className="text-white font-semibold text-sm">Platform Live</h3>
            <p className="text-white/40 text-xs">
              {ok
                ? `Scheduler running · auto-sync every ${status.auto_sync_interval_min} min · reaper every ${status.stuck_reaper_interval_min} min`
                : 'Scheduler not running — platform may be missing hourly jobs'}
            </p>
          </div>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full border ${
          ok
            ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20'
            : 'bg-rose-500/10 text-rose-300 border-rose-500/20'
        }`}>
          {ok ? 'healthy' : 'stopped'}
        </span>
      </div>

      <div className="p-3 space-y-1.5">
        {status.jobs.map(j => <JobRow key={j.id} job={j} />)}
      </div>

      <div className="px-5 py-3 border-t border-white/5 flex items-center justify-between">
        <p className="text-white/25 text-xs">
          Telemetry refreshes every 30s
        </p>
        <div className="flex items-center gap-2">
          <Lever
            icon={RefreshCw}
            label="Refresh"
            onClick={fetchStatus}
            disabled={!!firing}
            testId="scheduler-refresh-btn"
          />
          <Lever
            icon={Play}
            label="Run sync now"
            onClick={() => fire('sync', '/admin/scheduler/run-now', 'Auto-sync')}
            disabled={firing === 'sync'}
            testId="scheduler-run-sync-btn"
          />
          <Lever
            icon={Wrench}
            label="Retry stuck calls"
            onClick={() => fire('reap', '/admin/scheduler/reap-stuck-now', 'Stuck-call reaper')}
            disabled={firing === 'reap'}
            testId="scheduler-reap-btn"
          />
          <Lever
            icon={Users}
            label="Refresh Yabbit names"
            onClick={() => fire('names', '/admin/scheduler/refresh-agent-names-now', 'Yabbit name refresh')}
            disabled={firing === 'names'}
            testId="scheduler-name-refresh-btn"
          />
        </div>
      </div>
    </div>
  );
};
